import { useState } from "react";
import { ScheduleMatchResolutionKind } from "@common/types/domains/accounting/schedule";
import { Button } from "@design-system/Button/Button";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { HStack } from "@design-system/HStack/HStack";
import { PlainModal, StandardModalHeader } from "@design-system/Modal/Modal";
import { VStack } from "@design-system/VStack/VStack";
import { ScheduleOptionCards } from "../options/ScheduleOptionCards";
import { SCHEDULE_OPTIONS_BACK_LABEL } from "../options/ScheduleOptionsModal";
import { ScheduleConflictStepView } from "./scheduleConflictWizardCopy";
import styles from "./ScheduleConflictWizardModal.module.scss";

interface Props {
    show: boolean;
    view: ScheduleConflictStepView;
    onConfirm: (kind: ScheduleMatchResolutionKind) => Promise<void>;
    /** Back to the drawer with the selection intact. */
    onBack: () => void;
    /** Abandon the whole save. */
    onClose: () => void;
    onMatchOneAtATime: () => void;
    onExited: () => void;
}

/**
 * One step of the schedule conflict wizard: the invoice whose links would
 * carry two live schedules, and what to do about it. Mount with a `key` per
 * step so the picked option resets between invoices.
 */
export const ScheduleConflictWizardModal = ({
    show,
    view,
    onConfirm,
    onBack,
    onClose,
    onMatchOneAtATime,
    onExited,
}: Props) => {
    const [selectedKind, setSelectedKind] =
        useState<ScheduleMatchResolutionKind | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleConfirm = async () => {
        if (selectedKind) {
            await onConfirm(selectedKind);
        }
    };

    return (
        <PlainModal show={show} onClose={onClose} onExited={onExited}>
            <StandardModalHeader>{view.title}</StandardModalHeader>
            <VStack gap={24}>
                <VStack gap={8}>
                    <p className={styles.body}>{view.body}</p>
                    {view.extra && <p className={styles.body}>{view.extra}</p>}
                </VStack>
                <ScheduleOptionCards
                    ariaLabel={view.title}
                    options={view.options.map((option) => ({
                        value: option.kind,
                        title: option.title,
                        description: option.description,
                        isDisabled: isSubmitting || option.isDisabled,
                        hint: option.warning ?? undefined,
                    }))}
                    value={selectedKind}
                    onChange={setSelectedKind}
                />
                <HStack as="footer" gap={8} justify="between" align="center">
                    <div>
                        {view.showMatchOneAtATime && (
                            <Button
                                variant="ghost"
                                disabled={isSubmitting}
                                onClick={onMatchOneAtATime}
                            >
                                Match one invoice at a time
                            </Button>
                        )}
                    </div>
                    <HStack gap={8}>
                        <Button
                            type="button"
                            variant="secondary"
                            size="xl"
                            disabled={isSubmitting}
                            onClick={onBack}
                        >
                            {SCHEDULE_OPTIONS_BACK_LABEL}
                        </Button>
                        <ButtonWithLoader
                            variant="primary"
                            size="xl"
                            disabled={!selectedKind}
                            loading={isSubmitting}
                            setLoading={setIsSubmitting}
                            onClick={handleConfirm}
                            hotKey="mod+enter"
                            hotKeyOptions={{
                                enabled: show && selectedKind !== null,
                                description: "Confirm",
                                preventDefault: true,
                            }}
                            hotKeyDisplayPosition="inline"
                        >
                            Confirm
                        </ButtonWithLoader>
                    </HStack>
                </HStack>
            </VStack>
        </PlainModal>
    );
};
